import { Tune } from "@mui/icons-material";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import TawkChat from "../../../landing/components/TawkChat";
import { useMemorialPageState } from "./components/hooks/useMemorialPageState";
import ChurchTemplate1 from "./components/templates/church/template-1/ChurchTemplate1";
import ConferenceTemplate1 from "./components/templates/conference/template-1/ConferenceTemplate1";
import ConferenceStreamPage from "./components/templates/conference/template-2/SummitConferencePage";
import FuneralHomeTemplate from "./components/templates/funeral/funeral-home/FuneralHomeTemplate";
import FuneralTemplate1 from "./components/templates/funeral/template-1/FuneralTemplate1";
import FuneralTemplate2 from "./components/templates/funeral/template-2/FuneralTemplate2";
import TemplateSelectorDialog from "./components/shared-ui/TemplateSelectorDialog";
import RateExperienceModal from "./components/shared-ui/RateExperienceModal";
import { useEffect, useState } from "react";

const DEFAULT_DOCUMENT_TITLE = document.title;

function applyStreamSEO(stream: ReturnType<typeof useMemorialPageState>["currentStream"]) {
  if (!stream?.booking?.seo_active || !stream.booking.seo_metadata) {
    document.title = DEFAULT_DOCUMENT_TITLE;
    document.head.querySelectorAll("meta[data-booking-seo]").forEach((element) => element.remove());
    return;
  }
  const deceased = stream.booking.deceased?.[0];
  const venueName = stream.service?.venue?.name || stream.event_details?.venue?.name || stream.venue_name || "";
  const context: Record<string, string> = {
    deceased_name: [deceased?.first_name, deceased?.second_name, deceased?.surname].filter(Boolean).join(" "),
    deceased_first_name: deceased?.first_name || "",
    deceased_second_name: deceased?.second_name || "",
    deceased_surname: deceased?.surname || "",
    date_of_birth: deceased?.date_of_birth || "",
    date_of_death: deceased?.date_of_death || "",
    stream_title: stream.title || "",
    stream_date: stream.date ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(stream.date) ? `${stream.date}T12:00:00` : stream.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "",
    service_type: stream.booking.service || stream.booking.booking_type || "",
    location: [venueName, stream.city].filter(Boolean).join(", "),
    city: stream.city || "",
    venue_name: venueName,
    booking_id: String(stream.booking.id),
    booking_reference: stream.booking.reference_number || "",
  };
  const resolve = (value?: string) => (value || "").replace(/\{\{(\w+)\}\}/g, (_token, key: string) => context[key] ?? "");
  const title = resolve(stream.booking.seo_metadata.title);
  if (title) document.title = title;
  const setMeta = (name: string, content: string, property = false) => {
    if (!content) return;
    let element = document.head.querySelector<HTMLMetaElement>(`meta[data-booking-seo="${name}"]`);
    if (!element) {
      element = document.createElement("meta");
      element.dataset.bookingSeo = name;
      document.head.appendChild(element);
    }
    if (property) element.setAttribute("property", name);
    else element.name = name;
    element.content = content;
  };
  setMeta("description", resolve(stream.booking.seo_metadata.description));
  setMeta("keywords", resolve(stream.booking.seo_metadata.keywords));
  setMeta("og:title", title, true);
  setMeta("og:description", resolve(stream.booking.seo_metadata.description), true);
  setMeta("og:image", resolve(stream.booking.seo_metadata.og_image_url), true);
  setMeta("twitter:card", "summary_large_image");
  setMeta("twitter:title", title);
  setMeta("twitter:description", resolve(stream.booking.seo_metadata.description));
  setMeta("twitter:image", resolve(stream.booking.seo_metadata.og_image_url));
}

export default function MemorialPage() {
  const state = useMemorialPageState();
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const { isLoadingStreams, streams, currentStream, isAdmin, isStaging } =
    state;

  useEffect(() => {
    applyStreamSEO(currentStream);
  }, [currentStream]);

  if (isLoadingStreams) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography>Loading streams...</Typography>
      </Box>
    );
  }

  if (!streams.length) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography>No streams found for this booking.</Typography>
      </Box>
    );
  }

  if (
    currentStream?.booking?.service === "Conference" &&
    currentStream?.booking?.conference_type === "summit"
  ) {
    return (
      <>
        <ConferenceStreamPage />
        <RateExperienceModal streamId={currentStream.id} open={state.showRatingPrompt} onClose={state.dismissRatingPrompt} />
      </>
    );
  }

  const isFuneral =
    currentStream.booking.service === "Funeral" ||
    currentStream.booking.service === "Funeral_Home";

  const funeralHome = currentStream.booking.funeral_home;

  if (currentStream.booking.service === "Funeral_Home" && funeralHome?.name) {
    return (
      <>
      <FuneralHomeTemplate
        streams={streams}
        activeStreamIndex={state.activeStreamIndex}
        currentStream={currentStream}
        onStreamChange={state.handleStreamChange}
        memorialTheme={state.memorialTheme}
        deceasedData={state.deceasedData}
        isLoadingDeceased={state.isLoadingDeceased}
        messages={state.messages}
        isAdmin={isAdmin}
        socketRef={state.socketRef}
        handleApprove={state.handleApprove}
        handleDelete={state.handleDelete}
        onMessageSent={state.onMessageSent}
        currentPage={state.currentPage}
        totalCount={state.totalCount}
        pageSize={state.pageSize}
        hasNextPage={state.hasNextPage}
        hasPreviousPage={state.hasPreviousPage}
        onNextPage={state.onNextPage}
        onPreviousPage={state.onPreviousPage}
        funeralHomeName={funeralHome.name}
        funeralHomeLogo={funeralHome.company_logo}
        funeralHomeColor={funeralHome.theme?.primary_colors?.["400"]}
        funeralHomeWebsite={funeralHome.website}
        onBannerToggled={state.fetchStreamDetails}
        calendarEvent={state.streamCalendarEvent}
      />
      <RateExperienceModal streamId={currentStream.id} open={state.showRatingPrompt} onClose={state.dismissRatingPrompt} />
      </>
    );
  }

  if (isFuneral && currentStream.template_id === "funeral_2") {
    return (
      <>
        <TawkChat />
        {isAdmin && (
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 1 }}
          >
            <Tooltip title="Change page template">
              <Button
                size="small"
                startIcon={<Tune fontSize="small" />}
                onClick={() => setTemplateDialogOpen(true)}
                sx={{ textTransform: "none", borderRadius: "50px" }}
              >
                Change Template
              </Button>
            </Tooltip>
          </Box>
        )}
        <TemplateSelectorDialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          serviceType={currentStream.booking.service}
          streamId={currentStream.id}
          currentTemplateId={currentStream.template_id}
          onConfirm={() => {
            setTemplateDialogOpen(false);
            state.fetchStreamDetails();
          }}
        />
        <FuneralTemplate2
          streams={streams}
          activeStreamIndex={state.activeStreamIndex}
          currentStream={currentStream}
          onStreamChange={state.handleStreamChange}
          memorialTheme={state.memorialTheme}
          deceasedData={state.deceasedData}
          isLoadingDeceased={state.isLoadingDeceased}
          messages={state.messages}
          isAdmin={isAdmin}
          socketRef={state.socketRef}
          handleApprove={state.handleApprove}
          handleDelete={state.handleDelete}
          onMessageSent={state.onMessageSent}
          currentPage={state.currentPage}
          totalCount={state.totalCount}
          pageSize={state.pageSize}
          hasNextPage={state.hasNextPage}
          hasPreviousPage={state.hasPreviousPage}
          onNextPage={state.onNextPage}
          onPreviousPage={state.onPreviousPage}
          streamProgram={state.streamProgram}
          downloadingProgram={state.downloadingProgram}
          handleDownloadProgram={state.handleDownloadProgram}
          donationRefreshKey={state.donationRefreshKey}
          fetchStreamDetails={state.fetchStreamDetails}
          onDonationSuccess={state.onDonationSuccess}
          isStaging={isStaging}
          contactPhone={state.footerContactPhone}
          contactEmail={state.footerContactEmail}
          facebookUrl={state.footerFacebookUrl}
          linkedinUrl={state.footerLinkedinUrl}
          youtubeUrl={state.footerYoutubeUrl}
          whatsappUrl={state.footerWhatsappUrl}
          calendarEvent={state.streamCalendarEvent}
        />
        <RateExperienceModal streamId={currentStream.id} open={state.showRatingPrompt} onClose={state.dismissRatingPrompt} />
      </>
    );
  }

  if (
    currentStream.booking.service === "Church" ||
    currentStream.booking.service === "Conference" ||
    currentStream.booking.service === "Other"
  ) {
    const ConferenceOrChurchTemplate =
      currentStream.booking.service === "Conference"
        ? ConferenceTemplate1
        : ChurchTemplate1;

    return (
      <>
      <ConferenceOrChurchTemplate
        streams={streams}
        activeStreamIndex={state.activeStreamIndex}
        currentStream={currentStream}
        onStreamChange={state.handleStreamChange}
        memorialTheme={state.memorialTheme}
        deceasedName={state.deceasedData?.first_name}
        messages={state.messages}
        isAdmin={isAdmin}
        socketRef={state.socketRef}
        handleApprove={state.handleApprove}
        handleDelete={state.handleDelete}
        onMessageSent={state.onMessageSent}
        streamProgram={state.streamProgram}
        downloadingProgram={state.downloadingProgram}
        handleDownloadProgram={state.handleDownloadProgram}
        onDonationSuccess={state.onDonationSuccess}
        conferenceCalendarEvent={state.streamCalendarEvent}
        fetchStreamDetails={state.fetchStreamDetails}
        contactPhone={state.footerContactPhone}
        contactEmail={state.footerContactEmail}
        facebookUrl={state.footerFacebookUrl}
        linkedinUrl={state.footerLinkedinUrl}
        youtubeUrl={state.footerYoutubeUrl}
        whatsappUrl={state.footerWhatsappUrl}
      />
      <RateExperienceModal streamId={currentStream.id} open={state.showRatingPrompt} onClose={state.dismissRatingPrompt} />
      </>
    );
  }

  return (
    <>
    <FuneralTemplate1
      streams={streams}
      activeStreamIndex={state.activeStreamIndex}
      currentStream={currentStream}
      onStreamChange={state.handleStreamChange}
      memorialTheme={state.memorialTheme}
      deceasedData={state.deceasedData}
      isLoadingDeceased={state.isLoadingDeceased}
      messages={state.messages}
      isAdmin={isAdmin}
      socketRef={state.socketRef}
      handleApprove={state.handleApprove}
      handleDelete={state.handleDelete}
      onMessageSent={state.onMessageSent}
      currentPage={state.currentPage}
      totalCount={state.totalCount}
      pageSize={state.pageSize}
      hasNextPage={state.hasNextPage}
      hasPreviousPage={state.hasPreviousPage}
      onNextPage={state.onNextPage}
      onPreviousPage={state.onPreviousPage}
      streamProgram={state.streamProgram}
      downloadingProgram={state.downloadingProgram}
      handleDownloadProgram={state.handleDownloadProgram}
      donationRefreshKey={state.donationRefreshKey}
      fetchStreamDetails={state.fetchStreamDetails}
      onDonationSuccess={state.onDonationSuccess}
      isStaging={isStaging}
      contactPhone={state.footerContactPhone}
      contactEmail={state.footerContactEmail}
      facebookUrl={state.footerFacebookUrl}
      linkedinUrl={state.footerLinkedinUrl}
      youtubeUrl={state.footerYoutubeUrl}
      whatsappUrl={state.footerWhatsappUrl}
      calendarEvent={state.streamCalendarEvent}
    />
    <RateExperienceModal streamId={currentStream.id} open={state.showRatingPrompt} onClose={state.dismissRatingPrompt} />
    </>
  );
}
